import { z } from 'zod';
export * from './cg-custom-domain';

export const LANDING_PAGE_CONFIG_VERSION = 1;

export const LANDING_PAGE_SLUG_MIN_LENGTH = 2;
export const LANDING_PAGE_SLUG_MAX_LENGTH = 48;

/** Lowercase kebab-case: no leading, trailing or doubled hyphens. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Pages live at `/{slug}` next to Client Gateway's own routes and the bucket's `assets/` folder.
// Check new CG routes against existing slugs.
export const RESERVED_LANDING_PAGE_SLUGS: readonly string[] = [
  'sites',
  'conversations',
  'conversation',
  'memberships',
  'terms',
  'confirmation',
  'magic-login',
  'login',
  'assets',
  'api',
  'auth',
  'logout',
  'signup',
  'settings',
  'account',
  'help',
  'support',
  'static',
  'public',
  'terms-of-service',
  'privacy-policy',
];

export const LANDING_PAGE_SLUG_RESERVED_MESSAGE =
  'This address is reserved. Choose another one.';

export const landingPageSlugSchema = z
  .string()
  .min(LANDING_PAGE_SLUG_MIN_LENGTH)
  .max(LANDING_PAGE_SLUG_MAX_LENGTH)
  .regex(SLUG_PATTERN, 'Use lowercase letters, numbers and single hyphens.')
  .refine(
    (slug) => !RESERVED_LANDING_PAGE_SLUGS.includes(slug),
    LANDING_PAGE_SLUG_RESERVED_MESSAGE,
  );

export function isValidLandingPageSlug(value: unknown): value is string {
  return landingPageSlugSchema.safeParse(value).success;
}

/** Mirrors the `legal_document_kind` DB enum. */
export enum LegalDocumentKind {
  TermsOfService = 'terms_of_service',
  PrivacyPolicy = 'privacy_policy',
}

// Fixed, so a partner's old links keep working once their DNS points at us.
export const LEGAL_DOCUMENT_PATHS: Record<LegalDocumentKind, string> = {
  [LegalDocumentKind.TermsOfService]: '/terms-of-service',
  [LegalDocumentKind.PrivacyPolicy]: '/privacy-policy',
};

export const HERO_MEDIA_KINDS = ['none', 'video', 'image'] as const;
export const CONTENT_MEDIA_POSITIONS = ['auto', 'left', 'right'] as const;
// CSS `object-fit` values, so the renderer applies the stored value as is.
export const LANDING_PAGE_IMAGE_FITS = ['contain', 'cover', 'fill'] as const;
export const LANDING_PAGE_CTA_MODES = ['consultation', 'signup'] as const;
export const LANDING_PAGE_LOGO_SIZES = ['standard', 'large'] as const;
// `solid` and `autoGradient` paint from `color`; `image` from `imageUrl`.
export const LANDING_PAGE_BACKGROUND_FILLS = ['solid', 'autoGradient', 'image'] as const;

export type HeroMediaKind = (typeof HERO_MEDIA_KINDS)[number];
export type ContentMediaPosition = (typeof CONTENT_MEDIA_POSITIONS)[number];
export type LandingPageImageFit = (typeof LANDING_PAGE_IMAGE_FITS)[number];
export type LandingPageCtaMode = (typeof LANDING_PAGE_CTA_MODES)[number];
export type LandingPageLogoSize = (typeof LANDING_PAGE_LOGO_SIZES)[number];
export type LandingPageBackgroundFill = (typeof LANDING_PAGE_BACKGROUND_FILLS)[number];

export const DEFAULT_HERO_MEDIA_KIND: HeroMediaKind = 'none';
export const DEFAULT_CONTENT_MEDIA_POSITION: ContentMediaPosition = 'auto';
export const DEFAULT_LANDING_PAGE_IMAGE_FIT: LandingPageImageFit = 'contain';
export const DEFAULT_LANDING_PAGE_IMAGE_FOCUS = 50;
export const DEFAULT_LANDING_PAGE_BACKGROUND_FILL: LandingPageBackgroundFill = 'solid';

// Desktop logo heights in px. The presets are what `logoSize` meant before `header.logoHeight`.
export const LANDING_PAGE_LOGO_HEIGHTS: Record<LandingPageLogoSize, number> = {
  standard: 38,
  large: 62,
};
export const LANDING_PAGE_LOGO_HEIGHT_MIN = 24;
export const LANDING_PAGE_LOGO_HEIGHT_MAX = 120;
// A block's minimum height in px; 0 is auto, as tall as its content.
export const LANDING_PAGE_BLOCK_MIN_HEIGHT_MAX = 960;

// `plans` and `faq` take their content from the brand's activation flow config at render time;
// `plans` owns only its optional heading, which falls back to the renderer's default copy.
// Every block id doubles as an in-page nav anchor.
//
// `looseObject` puts an index signature on the inferred type, so a removed or renamed field
// reads as `unknown` at every call site rather than failing to compile. Grep, don't build.
// New enum fields take `.default()`, never required: one unparseable block makes the whole
// config null, which the portal then overwrites with the starter page.
// Which part of the image survives a `cover` crop, as CSS `object-position` percentages.
const imageFocus = z.number().min(0).max(100).default(DEFAULT_LANDING_PAGE_IMAGE_FOCUS);
const imageFocusFields = { imageFocusX: imageFocus, imageFocusY: imageFocus };

// Any block may paint its own band and stand taller than its content; absent means the layout's
// own background and height. `minHeight` is unbounded for the same reason as `logoHeight`.
const blockStyleFields = {
  background: z
    .looseObject({
      fill: z
        .enum(LANDING_PAGE_BACKGROUND_FILLS)
        .default(DEFAULT_LANDING_PAGE_BACKGROUND_FILL),
      color: z.string().optional(),
      imageUrl: z.string().optional(),
      imageFit: z.enum(LANDING_PAGE_IMAGE_FITS).default(DEFAULT_LANDING_PAGE_IMAGE_FIT),
      ...imageFocusFields,
    })
    .optional(),
  minHeight: z.number().optional(),
};

const blockSchema = z.discriminatedUnion('type', [
  z.looseObject({
    id: z.string(),
    type: z.literal('hero'),
    headline: z.string().min(1),
    subtext: z.string().optional(),
    // Three-valued so turning media off does not mean deleting the URL the user just pasted.
    mediaKind: z.enum(HERO_MEDIA_KINDS).default(DEFAULT_HERO_MEDIA_KIND),
    video: z
      .looseObject({
        mediaUrl: z.string(),
        title: z.string().optional(),
        caption: z.string().optional(),
      })
      .optional(),
    image: z.looseObject({ url: z.string() }).optional(),
    imageFit: z.enum(LANDING_PAGE_IMAGE_FITS).default(DEFAULT_LANDING_PAGE_IMAGE_FIT),
    ...imageFocusFields,
    ...blockStyleFields,
  }),
  z.looseObject({
    id: z.string(),
    type: z.literal('content'),
    kicker: z.string().optional(),
    title: z.string().min(1),
    body: z.string(),
    imageUrl: z.string().optional(),
    mediaPosition: z.enum(CONTENT_MEDIA_POSITIONS).default(DEFAULT_CONTENT_MEDIA_POSITION),
    imageFit: z.enum(LANDING_PAGE_IMAGE_FITS).default(DEFAULT_LANDING_PAGE_IMAGE_FIT),
    ...imageFocusFields,
    ...blockStyleFields,
  }),
  z.looseObject({
    id: z.string(),
    type: z.literal('plans'),
    kicker: z.string().optional(),
    title: z.string().optional(),
    subtext: z.string().optional(),
    ...blockStyleFields,
  }),
  z.looseObject({ id: z.string(), type: z.literal('faq'), ...blockStyleFields }),
  z.looseObject({
    id: z.string(),
    type: z.literal('closingCta'),
    headline: z.string().min(1),
    subtext: z.string().optional(),
    buttonLabel: z.string().min(1),
    ...blockStyleFields,
  }),
]);

export const landingPageConfigSchema = z.looseObject({
  version: z.number().int().positive(),
  blocks: z.array(blockSchema),
  settings: z.looseObject({
    showPricing: z.boolean(),
    cta: z.looseObject({
      mode: z.enum(LANDING_PAGE_CTA_MODES),
      consultationUrl: z.string().optional(),
    }),
    // Superseded by `header.logoHeight` when that is set; kept so older pages render unchanged.
    logoSize: z.enum(LANDING_PAGE_LOGO_SIZES),
    // Unbounded on purpose: an out-of-range height would fail the whole config, so
    // `resolveLandingPageLogoHeight` clamps it instead.
    header: z
      .looseObject({
        backgroundColor: z.string().optional(),
        textColor: z.string().optional(),
        logoHeight: z.number().optional(),
      })
      .optional(),
    colors: z.looseObject({
      primary: z.string().optional(),
      secondary: z.string().optional(),
      accent: z.string().optional(),
    }),
  }),
});

export type LandingPageBlock = z.infer<typeof blockSchema>;
export type LandingPageBlockType = LandingPageBlock['type'];
export type LandingPageConfig = z.infer<typeof landingPageConfigSchema>;

// Shared icons for the starter page's benefit sections, hosted for every partner's page.
const STARTER_ASSET_BASE =
  'https://res.cloudinary.com/smarthometech/image/upload/Landing%20Page%20Assets';

const DEFAULT_IMAGE_FRAME = {
  imageFit: DEFAULT_LANDING_PAGE_IMAGE_FIT,
  imageFocusX: DEFAULT_LANDING_PAGE_IMAGE_FOCUS,
  imageFocusY: DEFAULT_LANDING_PAGE_IMAGE_FOCUS,
};

const benefitBlock = (
  id: string,
  icon: string,
  copy: { kicker: string; title: string; body: string },
): LandingPageBlock => ({
  id,
  type: 'content',
  ...copy,
  imageUrl: `${STARTER_ASSET_BASE}/${icon}.png`,
  mediaPosition: DEFAULT_CONTENT_MEDIA_POSITION,
  ...DEFAULT_IMAGE_FRAME,
});

/**
 * The starter page every brand begins from, so nobody opens a blank editor: the baseline every
 * partner page was rolled out from (Tutone's), minus anything of Tutone's own. The logo comes from
 * the brand. Block ids are stable readable anchors (#rapid-response), not uuids.
 */
export const DEFAULT_LANDING_PAGE_CONFIG: LandingPageConfig = {
  version: LANDING_PAGE_CONFIG_VERSION,
  blocks: [
    {
      id: 'hero',
      type: 'hero',
      mediaKind: 'none',
      ...DEFAULT_IMAGE_FRAME,
      headline: 'Choose your support experience',
      subtext:
        'Our premium support memberships provide access to remote assistance, priority scheduling, and more. Every membership is month-to-month with no commitment. Cancel any time.',
    },
    {
      id: 'plans',
      type: 'plans',
      title: 'Plans for whatever level of service you need',
      subtext: 'Our memberships are designed to give you flexibility and peace of mind',
    },
    benefitBlock('rapid-response', 'clock', {
      kicker: 'Rapid response 24/7',
      title: 'Guaranteed response times, around the clock',
      body: "Our memberships offer guaranteed response times from our remote support team 24/7/365. If the issue can't be resolved remotely, our advanced support team will be in touch within 1 hour to pick up where the remote team left off.",
    }),
    benefitBlock('priority-scheduling', 'calendar', {
      kicker: 'Prioritized scheduling',
      title: 'Night and weekend appointments, front of the line',
      body: "When an on-site service call is needed, who wants to wait? Our members enjoy the convenience of night and weekend on-site support for urgent issues. Additionally, members jump to the front of the line with prioritized scheduling. It's the peace of mind that professional help is available when you need it.",
    }),
    benefitBlock('proactive-monitoring', 'proactive', {
      kicker: 'Proactive system monitoring',
      title: 'We catch problems before you do',
      body: "Wouldn't it be nice if someone could remotely monitor the health of your system? Our service team is trained to identify problems, begin troubleshooting, and put intelligent solutions into motion before the problem becomes an inconvenience.",
    }),
    benefitBlock('service-discounts', 'invoice', {
      kicker: 'Exclusive service discounts',
      title: 'Preferred rates for members',
      body: 'Everyone likes a deal, right? Members receive preferred labor rates and other exclusive service discounts.',
    }),
    { id: 'faq', type: 'faq' },
    {
      id: 'get-started',
      type: 'closingCta',
      headline: 'Ready for a better technology experience?',
      subtext:
        'Our team of technology experts can answer any questions you might have and help you select the right membership for you.',
      buttonLabel: 'Sign Up Now',
    },
  ],
  settings: {
    showPricing: true,
    cta: { mode: 'signup' },
    logoSize: 'standard',
    colors: { primary: '#3B97D3', secondary: '#0E2A57', accent: '#F68E15' },
  },
};

export const SINGLETON_BLOCK_TYPES: ReadonlySet<LandingPageBlockType> = new Set([
  'hero',
  'plans',
  'faq',
  'closingCta',
]);

export function isSingletonBlockType(type: LandingPageBlockType): boolean {
  return SINGLETON_BLOCK_TYPES.has(type);
}

/** The desktop logo height a page renders: its own height, clamped, else its size preset's. */
export function resolveLandingPageLogoHeight(settings: LandingPageConfig['settings']): number {
  const height = settings.header?.logoHeight;
  if (height === undefined) return LANDING_PAGE_LOGO_HEIGHTS[settings.logoSize];

  return Math.min(
    LANDING_PAGE_LOGO_HEIGHT_MAX,
    Math.max(LANDING_PAGE_LOGO_HEIGHT_MIN, Math.round(height)),
  );
}

/** The minimum height a block renders at, rounded and clamped; 0 when it fits its content. */
export function resolveLandingPageBlockMinHeight(block: LandingPageBlock): number {
  return Math.min(
    LANDING_PAGE_BLOCK_MIN_HEIGHT_MAX,
    Math.max(0, Math.round(block.minHeight ?? 0)),
  );
}

export function isValidLandingPageUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Rules the schema deliberately does not carry. Folding these into `landingPageConfigSchema`
 * would make `parseLandingPageConfig` return null for a config that breaks one, and the portal
 * reads null as "unconfigured" and offers the starter page — so a save would replace real
 * content. Callers run this after a successful parse and surface the message instead.
 */
export function findLandingPageConfigIssue(config: LandingPageConfig): string | null {
  const seenIds = new Set<string>();
  const seenSingletons = new Set<LandingPageBlockType>();

  for (const block of config.blocks) {
    if (seenIds.has(block.id)) {
      return `Duplicate block id "${block.id}". Ids are page anchors and must be unique.`;
    }
    seenIds.add(block.id);

    if (isSingletonBlockType(block.type)) {
      if (seenSingletons.has(block.type)) {
        return `A landing page can hold only one "${block.type}" block.`;
      }
      seenSingletons.add(block.type);
    }
  }

  return findInvalidUrl(config);
}

function findInvalidUrl(config: LandingPageConfig): string | null {
  const urls: (string | undefined)[] = [config.settings.cta.consultationUrl];

  for (const block of config.blocks) {
    urls.push(block.background?.imageUrl);
    if (block.type === 'hero') {
      urls.push(block.video?.mediaUrl, block.image?.url);
    } else if (block.type === 'content') {
      urls.push(block.imageUrl);
    }
  }

  const invalid = urls.find((url) => url !== undefined && !isValidLandingPageUrl(url));

  return invalid === undefined ? null : `"${invalid}" is not a valid http(s) URL.`;
}

/**
 * Parse a stored config. Accepts a JSON string or an already-parsed object: the DB hands back
 * a string (db-layer/connection.ts disables the JSONB type parser) while the portal holds a
 * live form object.
 *
 * Returns null rather than throwing. The column defaults to '{}', so null is the normal state
 * for a brand that has not been configured yet — not an error worth logging.
 */
export function parseLandingPageConfig(raw: unknown): LandingPageConfig | null {
  const candidate = toParseCandidate(raw);
  if (candidate === null) return null;

  const result = landingPageConfigSchema.safeParse(candidate);
  return result.success ? result.data : null;
}

function toParseCandidate(raw: unknown): unknown {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== 'string') return raw;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}
